// hooks/usePersistedToggle.js
// ─────────────────────────────────────────────────────────────────────────────
// A boolean view preference that survives a refresh.
//
// REPORTED 27 Sep 2026: Brand Management's "Show Inactive" switch does not
// persist. It was `useState(false)`, so every reload, every navigation away and
// back, and every hot reload put the list back to active-only — and an operator
// working through inactive brands had to re-flick it each time, or worse,
// believed the inactive ones had gone.
//
// Now the boolean case of hooks/usePersistedPreference.js, which holds the
// storage rules (and the per-user keying the filter chips needed). Kept as its
// own hook so a toggle reads as a toggle at the call site and so the values
// browsers already stored under this key keep working: "true"/"false" parse as
// booleans under JSON.parse.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback } from "react";
import usePersistedPreference from "./usePersistedPreference";

/**
 * @param {string}  key            stable name, namespaced under lusuku.pref.
 * @param {boolean} [defaultValue] what to use when nothing is stored
 * @returns {[boolean, function]}  same shape as useState
 */
const usePersistedToggle = (key, defaultValue = false) => {
  const [value, setValue] = usePersistedPreference(key, !!defaultValue);

  const set = useCallback(
    (next) => {
      setValue((prev) => (typeof next === "function" ? !!next(!!prev) : !!next));
    },
    [setValue]
  );

  return [!!value, set];
};

export default usePersistedToggle;
